import { Button, ResponsiveTable, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { downloadCredentialed, openCredentialed } from '../../lib/download.js';
import type {
  FleetAssignmentHistoryDto,
  FleetDocumentDto,
  FleetRecentEntryDto,
  FleetReminderDto,
  FleetVehicleDto,
} from './contractsMirror.js';
import type { EntryDraft } from './EntryFormDrawer.js';
import { EntryFormDrawer } from './EntryFormDrawer.js';
import { fleetDocumentFileUrl, fleetEntryReceiptUrl, fleetPacketUrl } from './exportLinks.js';
import {
  useArchiveFleetDocumentMutation,
  useArchiveFleetEntryMutation,
  useArchiveFleetVehicleMutation,
  useAssignDriverMutation,
  useFleetDriversQuery,
  useFleetVehicleDetailQuery,
  useRestoreFleetDocumentMutation,
  useRestoreFleetEntryMutation,
  useRestoreFleetVehicleMutation,
  useUploadFleetDocumentMutation,
} from './useFleetQuery.js';

const DOCUMENT_TYPES = [
  'registration',
  'insurance',
  'inspection',
  'service_contract',
  'other',
] as const;

/**
 * The `/fleet/$id` vehicle-detail route (22-26). Fires the dedicated detail read
 * (`GET /api/v1/fleet/vehicles/:id`) so the vehicle overview, maintenance reminders, real document
 * rows, and assignment/attachment history all come from ONE tenant-scoped payload. Renders a
 * `role="tablist"` set (Overview · Maintenance Schedule · Documents), an Edit link, Archive/Restore
 * actions, and a driver-assignment form. No tenant id is ever sent (SEC-11).
 */

const routeApi = getRouteApi('/fleet/$id');

type TabKey = 'overview' | 'schedule' | 'documents';
const TAB_ORDER: readonly TabKey[] = ['overview', 'schedule', 'documents'];

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';

export function VehicleDetailRoute() {
  const { t } = useTranslation();
  const { id } = routeApi.useParams();
  const vehicleId = Number(id);

  const query = useFleetVehicleDetailQuery(vehicleId);
  const driversQuery = useFleetDriversQuery();
  const archiveMutation = useArchiveFleetVehicleMutation();
  const restoreMutation = useRestoreFleetVehicleMutation();
  const assignMutation = useAssignDriverMutation(vehicleId);
  const archiveEntryMutation = useArchiveFleetEntryMutation();
  const restoreEntryMutation = useRestoreFleetEntryMutation();
  const uploadDocumentMutation = useUploadFleetDocumentMutation();
  const archiveDocumentMutation = useArchiveFleetDocumentMutation();
  const restoreDocumentMutation = useRestoreFleetDocumentMutation();

  const [entryDraft, setEntryDraft] = useState<EntryDraft | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const tablistId = useId();
  const tabRefs = useRef<Record<TabKey, HTMLButtonElement | null>>({
    overview: null,
    schedule: null,
    documents: null,
  });

  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignDriverName, setAssignDriverName] = useState('');

  const vehicle = query.data?.vehicle;
  useEffect(() => {
    if (!vehicle) return;
    setAssignEmployeeId(vehicle.assignedEmployeeId ? String(vehicle.assignedEmployeeId) : '');
    setAssignDriverName(vehicle.assignedDriverName ?? '');
  }, [vehicle]);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError || !vehicle) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('fleet:error.detailHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('fleet:error.detailBody')}</p>
        <Button asChild variant="secondary">
          <Link to="/fleet">{t('fleet:actions.backToFleet')}</Link>
        </Button>
      </section>
    );
  }

  const reminders: FleetReminderDto[] = query.data?.reminders ?? [];
  const documents: FleetDocumentDto[] = query.data?.documents ?? [];
  const assignmentHistory: FleetAssignmentHistoryDto[] = query.data?.assignmentHistory ?? [];
  const recentEntries: FleetRecentEntryDto[] = query.data?.recentEntries ?? [];
  const drivers = driversQuery.data?.drivers ?? [];
  const archived = vehicle.archivedAt !== null;

  // A minimal single-vehicle option list so the entry drawer's picker resolves this vehicle.
  const drawerVehicles: FleetVehicleDto[] = [vehicle];

  function onDocumentUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setUploadError(null);
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const file = form.get('document');
    if (!(file instanceof File) || file.size <= 0) {
      setUploadError(t('fleet:documents.fileRequired'));
      return;
    }
    uploadDocumentMutation.mutate(
      {
        vehicle_id: vehicleId,
        document_type: String(form.get('document_type') ?? 'other') as
          | 'registration'
          | 'insurance'
          | 'inspection'
          | 'service_contract'
          | 'other',
        title: String(form.get('title') ?? '').trim(),
        file,
        expiration_date: String(form.get('expiration_date') ?? '').trim() || null,
      },
      {
        onSuccess: () => formEl.reset(),
        onError: (err) => setUploadError(err.message),
      },
    );
  }

  function onTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const currentIndex = TAB_ORDER.indexOf(activeTab);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TAB_ORDER.length;
    else if (event.key === 'ArrowLeft')
      nextIndex = (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TAB_ORDER.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextKey = TAB_ORDER[nextIndex];
    setActiveTab(nextKey);
    tabRefs.current[nextKey]?.focus();
  }

  function tabProps(key: TabKey) {
    const selected = activeTab === key;
    return {
      type: 'button' as const,
      role: 'tab' as const,
      id: `${tablistId}-tab-${key}`,
      'aria-selected': selected,
      'aria-controls': `${tablistId}-panel-${key}`,
      tabIndex: selected ? 0 : -1,
      ref: (node: HTMLButtonElement | null) => {
        tabRefs.current[key] = node;
      },
      onClick: () => setActiveTab(key),
      onKeyDown: onTabKeyDown,
      className: [
        'inline-flex h-11 items-center border-b-2 px-4 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        selected
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      ].join(' '),
    };
  }

  function panelProps(key: TabKey) {
    return {
      role: 'tabpanel' as const,
      id: `${tablistId}-panel-${key}`,
      'aria-labelledby': `${tablistId}-tab-${key}`,
      hidden: activeTab !== key,
      tabIndex: 0,
    };
  }

  function onAssignSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    assignMutation.mutate({
      assigned_employee_id: assignEmployeeId ? Number(assignEmployeeId) : null,
      assigned_driver_name: assignDriverName.trim() || null,
    });
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {vehicle.displayName}
          </h1>
          <span
            className={
              archived
                ? 'inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 font-medium text-muted-foreground text-xs'
                : 'inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary-ink text-xs'
            }
          >
            {archived ? t('fleet:status.archived') : t('fleet:status.active')}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="secondary">
            <Link to="/fleet/$id/edit" params={{ id }}>
              {t('fleet:actions.edit')}
            </Link>
          </Button>
          {archived ? (
            <Button
              variant="secondary"
              disabled={restoreMutation.isPending}
              onClick={() => restoreMutation.mutate(vehicle.id)}
            >
              {t('fleet:actions.restore')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              disabled={archiveMutation.isPending}
              onClick={() => archiveMutation.mutate(vehicle.id)}
            >
              {t('fleet:actions.archive')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() =>
              void downloadCredentialed(
                fleetPacketUrl(vehicle.id),
                `vehicle-${vehicle.id}-packet.pdf`,
              ).catch((err) => console.error('fleet packet download failed', err))
            }
          >
            <Download className="size-4" aria-hidden="true" />
            {t('fleet:actions.downloadPacket')}
          </Button>
          <Button asChild variant="secondary">
            <Link to="/fleet">{t('fleet:actions.back')}</Link>
          </Button>
        </div>
      </header>

      <div
        role="tablist"
        aria-label={t('fleet:detail.tablistLabel')}
        className="mb-6 flex flex-wrap gap-2 border-border border-b"
      >
        <button {...tabProps('overview')}>{t('fleet:detail.tabs.overview')}</button>
        <button {...tabProps('schedule')}>{t('fleet:detail.tabs.schedule')}</button>
        <button {...tabProps('documents')}>{t('fleet:detail.tabs.documents')}</button>
      </div>

      <section {...panelProps('overview')}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
            <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
              {t('fleet:detail.overviewHeading')}
            </h2>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <DetailField label={t('fleet:card.unit')} value={vehicle.unitNumber} t={t} />
              <DetailField label={t('fleet:card.plate')} value={vehicle.licensePlate} t={t} mono />
              <DetailField label={t('fleet:detail.make')} value={vehicle.make} t={t} />
              <DetailField label={t('fleet:detail.model')} value={vehicle.model} t={t} />
              <DetailField
                label={t('fleet:detail.year')}
                value={vehicle.year ? String(vehicle.year) : null}
                t={t}
              />
              <DetailField label={t('fleet:detail.vin')} value={vehicle.vin} t={t} mono />
              <DetailField
                label={t('fleet:detail.assignedTo')}
                value={vehicle.assignedEmployeeName ?? vehicle.assignedDriverName}
                t={t}
              />
            </dl>
          </div>
          <div className="rounded-md border border-border bg-card p-6 lg:col-span-1">
            <h2 className="font-display font-semibold text-foreground text-lg">
              {t('fleet:detail.notesHeading')}
            </h2>
            <p className="mt-2 text-foreground text-sm">
              {vehicle.notes ? vehicle.notes : t('fleet:detail.noNotes')}
            </p>
          </div>
        </div>

        {/* Driver assignment form + history (F9). */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-md border border-border bg-card p-6">
            <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
              {t('fleet:assignment.heading')}
            </h2>
            <form onSubmit={onAssignSubmit} className="space-y-4">
              <div>
                <label htmlFor="assign-driver" className={LABEL_CLASS}>
                  {t('fleet:assignment.driver')}
                </label>
                <select
                  id="assign-driver"
                  value={assignEmployeeId}
                  onChange={(event) => setAssignEmployeeId(event.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="">{t('fleet:assignment.noDriver')}</option>
                  {drivers.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="assign-driver-label" className={LABEL_CLASS}>
                  {t('fleet:assignment.driverLabel')}
                </label>
                <input
                  id="assign-driver-label"
                  value={assignDriverName}
                  onChange={(event) => setAssignDriverName(event.target.value)}
                  maxLength={120}
                  className={INPUT_CLASS}
                />
              </div>
              {assignMutation.isError ? (
                <p role="alert" className="text-destructive text-sm">
                  {t('fleet:assignment.error')}
                </p>
              ) : null}
              {assignMutation.isSuccess ? (
                <p role="status" className="text-secondary-foreground text-sm">
                  {t('fleet:assignment.success')}
                </p>
              ) : null}
              <Button type="submit" disabled={assignMutation.isPending}>
                {t('fleet:assignment.save')}
              </Button>
            </form>
          </div>

          <div className="rounded-md border border-border bg-card p-6">
            <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
              {t('fleet:assignment.historyHeading')}
            </h2>
            {assignmentHistory.length > 0 ? (
              <ul className="space-y-3 text-sm">
                {assignmentHistory.map((h) => (
                  <li key={h.id} className="border-border/60 border-b pb-3 last:border-0 last:pb-0">
                    <p className="text-foreground">
                      {(h.previousEmployeeName ?? h.previousDriverName ?? t('fleet:card.none')) +
                        ' → ' +
                        (h.newEmployeeName ?? h.newDriverName ?? t('fleet:card.none'))}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {h.changedAt}
                      {h.changedByUserName ? ` · ${h.changedByUserName}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">{t('fleet:assignment.noHistory')}</p>
            )}
          </div>
        </div>
      </section>

      <section {...panelProps('schedule')}>
        <div className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('fleet:detail.scheduleHeading')}
          </h2>
          {reminders.length > 0 ? (
            <ul className="space-y-3">
              {reminders.map((reminder) => (
                <li
                  key={reminder.category}
                  className="flex flex-wrap items-center justify-between gap-2 border-border/60 border-b pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="font-medium text-foreground text-sm">{reminder.label}</p>
                    <p className="text-muted-foreground text-xs">{reminder.reason}</p>
                  </div>
                  <span
                    className={
                      reminder.isDue
                        ? 'inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive text-xs'
                        : 'inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary-ink text-xs'
                    }
                  >
                    {reminder.isDue ? t('fleet:reminder.due') : t('fleet:reminder.upToDate')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">{t('fleet:detail.noSchedule')}</p>
          )}
        </div>

        {/* Recent entries with edit / archive / restore + receipt View (F13/F14). */}
        <div className="mt-6 rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('fleet:entries.heading')}
          </h2>
          {recentEntries.length > 0 ? (
            <ul className="space-y-3">
              {recentEntries.map((entry) => {
                const entryArchived = entry.archivedAt !== null;
                return (
                  <li
                    key={entry.id}
                    className="flex flex-wrap items-center justify-between gap-2 border-border/60 border-b pb-3 last:border-0 last:pb-0"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground text-sm">
                        {t(`fleet:entryType.${entry.entryType}`)} · {entry.entryDate} · $
                        {entry.amount.toFixed(2)}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {entry.vendor ?? t('fleet:card.none')}
                        {entryArchived ? ` · ${t('fleet:status.archived')}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {entry.receiptFilename ? (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            void openCredentialed(fleetEntryReceiptUrl(entry.id)).catch((err) =>
                              console.error('receipt view failed', err),
                            )
                          }
                        >
                          {t('fleet:entries.viewReceipt')}
                        </Button>
                      ) : null}
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setEntryDraft({
                            id: entry.id,
                            vehicleId: vehicle.id,
                            entryType: entry.entryType,
                            entryDate: entry.entryDate,
                            vendor: entry.vendor,
                            amount: entry.amount,
                            odometer: entry.odometer,
                            maintenanceCategory: entry.maintenanceCategory,
                            hasReceipt: entry.receiptFilename !== null,
                          })
                        }
                      >
                        {t('fleet:actions.edit')}
                      </Button>
                      {entryArchived ? (
                        <Button
                          variant="secondary"
                          disabled={restoreEntryMutation.isPending}
                          onClick={() => restoreEntryMutation.mutate(entry.id)}
                        >
                          {t('fleet:actions.restore')}
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          disabled={archiveEntryMutation.isPending}
                          onClick={() => archiveEntryMutation.mutate(entry.id)}
                        >
                          {t('fleet:actions.archive')}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">{t('fleet:entries.empty')}</p>
          )}
        </div>
      </section>

      <section {...panelProps('documents')}>
        <div className="rounded-md border border-border bg-card p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display font-semibold text-foreground text-lg">
              {t('fleet:detail.documentsHeading')}
            </h2>
            <Button
              variant="secondary"
              onClick={() =>
                void downloadCredentialed(
                  fleetPacketUrl(vehicle.id),
                  `vehicle-${vehicle.id}-packet.pdf`,
                ).catch((err) => console.error('fleet packet download failed', err))
              }
            >
              <Download className="size-4" aria-hidden="true" />
              {t('fleet:actions.downloadPacket')}
            </Button>
          </div>
          {/* Upload a typed document with an optional expiration (F16). */}
          <form
            onSubmit={onDocumentUpload}
            className="mb-6 grid gap-3 rounded-md border border-border border-dashed p-4 sm:grid-cols-2"
          >
            <div>
              <label htmlFor="doc-title" className={LABEL_CLASS}>
                {t('fleet:documents.title')}
              </label>
              <input id="doc-title" name="title" required maxLength={160} className={INPUT_CLASS} />
            </div>
            <div>
              <label htmlFor="doc-type" className={LABEL_CLASS}>
                {t('fleet:documents.type')}
              </label>
              <select
                id="doc-type"
                name="document_type"
                defaultValue="registration"
                className={INPUT_CLASS}
              >
                {DOCUMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`fleet:documentType.${type}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="doc-expiration" className={LABEL_CLASS}>
                {t('fleet:documents.expiration')}
              </label>
              <input
                id="doc-expiration"
                name="expiration_date"
                type="date"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label htmlFor="doc-file" className={LABEL_CLASS}>
                {t('fleet:documents.file')}
              </label>
              <input
                id="doc-file"
                name="document"
                type="file"
                accept="image/*,.pdf"
                required
                className="w-full text-foreground text-sm"
              />
            </div>
            {uploadError ? (
              <p role="alert" className="text-destructive text-sm sm:col-span-2">
                {uploadError}
              </p>
            ) : null}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={uploadDocumentMutation.isPending}>
                {t('fleet:documents.upload')}
              </Button>
            </div>
          </form>

          {documents.length > 0 ? (
            // The only table in the route with no scroller of its own: at 390px its
            // five columns pushed the whole PAGE 77px sideways. `ResponsiveTable`
            // both contains it and reflows the rows to cards below `md`.
            <ResponsiveTable>
              <table className="w-full text-left text-sm">
                <caption className="sr-only">{t('fleet:documents.caption')}</caption>
                <thead>
                  <tr className="border-border border-b text-muted-foreground text-xs uppercase tracking-wide">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('fleet:documents.title')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('fleet:documents.type')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('fleet:documents.expiration')}
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      {t('fleet:documents.status')}
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      <span className="sr-only">{t('fleet:documents.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((document) => {
                    const docArchived = document.archivedAt !== null;
                    return (
                      <tr key={document.id} className="border-border/60 border-b last:border-0">
                        <td className="py-2 pr-4 text-foreground">{document.title}</td>
                        <td className="py-2 pr-4 text-foreground">
                          {t(`fleet:documentType.${document.documentType}`)}
                        </td>
                        <td className="py-2 pr-4 text-foreground">
                          {document.expirationDate ?? t('fleet:card.none')}
                        </td>
                        <td className="py-2 pr-4 text-foreground">
                          {docArchived ? t('fleet:status.archived') : t('fleet:status.active')}
                        </td>
                        <td className="py-2">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Button
                              variant="secondary"
                              onClick={() =>
                                void openCredentialed(fleetDocumentFileUrl(document.id)).catch(
                                  (err) => console.error('document view failed', err),
                                )
                              }
                            >
                              {t('fleet:documents.view')}
                            </Button>
                            {docArchived ? (
                              <Button
                                variant="secondary"
                                disabled={restoreDocumentMutation.isPending}
                                onClick={() => restoreDocumentMutation.mutate(document.id)}
                              >
                                {t('fleet:actions.restore')}
                              </Button>
                            ) : (
                              <Button
                                variant="secondary"
                                disabled={archiveDocumentMutation.isPending}
                                onClick={() => archiveDocumentMutation.mutate(document.id)}
                              >
                                {t('fleet:actions.archive')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ResponsiveTable>
          ) : (
            <p className="text-muted-foreground text-sm">{t('fleet:documents.empty')}</p>
          )}
        </div>
      </section>

      <EntryFormDrawer
        open={entryDraft !== null}
        mode="edit"
        vehicles={drawerVehicles}
        entry={entryDraft}
        onClose={() => setEntryDraft(null)}
      />
    </div>
  );
}

function DetailField({
  label,
  value,
  t,
  mono = false,
}: {
  label: string;
  value: string | null;
  t: (key: string) => string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
      <dd className={mono ? 'truncate font-mono text-foreground' : 'truncate text-foreground'}>
        {value ? value : t('fleet:card.none')}
      </dd>
    </div>
  );
}
